import React, { useState, useEffect } from 'react';
import { Switch, message, Spin, Typography, Tag } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import { StandardCard } from '../common';
import { theme } from '../../theme';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

interface Presentacion {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  alta: number;
}

const Presentacion: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  
  const id_empresa = 1;
  const id_sucursal = 2;

  const fetchPresentacion = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await apiClient.get<Presentacion[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PRE`,
        { token }
      );

      const isEmpty = !response.data || response.data.length === 0;
      
      if (isEmpty) {
        setIsActive(false);
      } else {
        setIsActive(response.data[0].alta === 1);
      }

    } catch (error) {
      console.error('Error al obtener datos:', error);
      messageApi.error('Error al cargar los datos iniciales');
      setIsActive(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresentacion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchChange = async (checked: boolean) => {
    // Actualizar estado optimísticamente
    const previousState = isActive;
    setIsActive(checked);
    
    try {
      const token = await getAccessTokenSilently();
      
      if (checked) {
        // Activar
        await apiClient.post(
          `${process.env.REACT_APP_RELACION_URL}/grabar_datos_fijos/${id_empresa}/${id_sucursal}/PRE/''/0`,
          {},
          { token }
        );
        messageApi.success('Presentación activada correctamente');
      } else {
        // Desactivar
        await apiClient.post(
          `${process.env.REACT_APP_RELACION_URL}/desactivar_datos_fijos/${id_empresa}/${id_sucursal}/PRE`,
          {},
          { token }
        );
        messageApi.success('Presentación desactivada correctamente');
      }
      
      // No recargar todo el componente, solo mantener el estado actualizado

    } catch (error) {
      console.error('Error:', error);
      messageApi.error(checked ? 'Error al activar la presentación' : 'Error al desactivar la presentación');
      // Revertir el estado en caso de error
      setIsActive(previousState);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <StandardCard
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InfoCircleOutlined />
            Presentación Algodón en Bruto
          </span>
        }
      >
        <div style={{ marginBottom: '24px' }}>
          <Paragraph style={{ 
            textAlign: 'justify',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Presentación de Módulos/Rollos evalúa visualmente en que condiciones envía el Cliente el Algodón en bruto.
            Generalmente va a depósito para ser desmotado en otro momento.
          </Paragraph>
          <Paragraph style={{ 
            textAlign: 'justify',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Se evalúa Apretado, Limpieza y Tapado en ese orden y desde el 1 al 5 siendo este último el valor óptimo.
          </Paragraph>
          <Paragraph style={{ 
            textAlign: 'justify',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            fontSize: '14px',
            marginBottom: 0
          }}>
            Esto dato prevee dificultades futuras con la carga por ganancia de humedad y/o dificultades en el traslado a desmote.
          </Paragraph>
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: isActive ? '#f6ffed' : '#fff7e6',
          borderRadius: theme.styles.borderRadius,
          border: `2px solid ${isActive ? '#b7eb8f' : '#ffe58f'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <Text strong style={{ fontSize: '14px', color: theme.colors.textPrimary }}>
              Estado:
            </Text>
            <Tag 
              color={isActive ? 'success' : 'warning'} 
              style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 600 }}
            >
              {isActive ? 'Activa' : 'Inactiva'}
            </Tag>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <Text style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
              {isActive ? 'Presentación activa' : 'Presentación inactiva'}
            </Text>
            <Switch
              checked={isActive}
              onChange={handleSwitchChange}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              size="small"
            />
          </div>
        </div>
      </StandardCard>
    </>
  );
};

export default Presentacion;
